import { readFreshUtmData } from "./utm";

const JOURNEY_STORAGE_KEY = "hv_contact_journey_v1";
const JOURNEY_IDLE_TTL_MS = 30 * 60 * 1000;
const MAX_PATH_LENGTH = 240;

let memoryJourney = null;
let pageCaptureInitialized = false;

function nowMs() {
  return Date.now();
}

function randomHex(bytes = 12) {
  try {
    if (window.crypto && typeof window.crypto.getRandomValues === "function") {
      const values = new Uint8Array(bytes);
      window.crypto.getRandomValues(values);
      return Array.from(values, (value) => value.toString(16).padStart(2, "0")).join("");
    }
  } catch (e) {
    // Fall through to the compatibility fallback below.
  }

  let value = "";
  while (value.length < bytes * 2) {
    value += Math.random().toString(16).slice(2);
  }
  return value.slice(0, bytes * 2);
}

export function generateContactEventId(prefix = "HC") {
  return prefix + "_" + randomHex(12);
}

export function sanitizeJourneyPath(value) {
  if (typeof value !== "string" || !value) return "/";

  try {
    const parsed = new URL(value, window.location.origin);
    return (parsed.pathname || "/").slice(0, MAX_PATH_LENGTH);
  } catch (e) {
    const path = value.split(/[?#]/, 1)[0];
    return (path && path.charAt(0) === "/" ? path : "/").slice(0, MAX_PATH_LENGTH);
  }
}

function currentPath() {
  return sanitizeJourneyPath(window.location.pathname || "/");
}

function measurementChoice() {
  const state = window.hvOpenAiMeasurement || {};
  return {
    measurement_consent: state.measurement_consent || "unknown",
    measurement_consent_at: state.measurement_consent_at || "",
    openai_measurement_permission: state.openai_measurement_permission || "unknown",
  };
}

function validJourney(value, currentTime) {
  return value
    && typeof value === "object"
    && /^HJ_[a-f0-9]{24}$/.test(value.journey_id || "")
    && typeof value.landing_path === "string"
    && Number.isFinite(Number(value.last_seen_at))
    && currentTime - Number(value.last_seen_at) <= JOURNEY_IDLE_TTL_MS
    && currentTime >= Number(value.last_seen_at) - 5 * 60 * 1000;
}

function loadStoredJourney(currentTime) {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(JOURNEY_STORAGE_KEY));
    return validJourney(parsed, currentTime) ? parsed : null;
  } catch (e) {
    return validJourney(memoryJourney, currentTime) ? memoryJourney : null;
  }
}

function saveJourney(journey) {
  memoryJourney = journey;
  try {
    window.sessionStorage.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(journey));
  } catch (e) {
    // Private browsing/storage policies can reject sessionStorage. The
    // in-memory value still preserves identity for the current page.
  }
}

export function getJourneyContext() {
  const currentTime = nowMs();
  const stored = loadStoredJourney(currentTime);
  const journey = stored || {
    journey_id: "HJ_" + randomHex(12),
    landing_path: currentPath(),
    started_at: currentTime,
  };

  if (!pageCaptureInitialized) {
    const meta = document.querySelector('meta[name="hv-acquisition-capture"]');
    const capture = meta ? meta.content : "";
    let internalNavigation = false;
    try {
      internalNavigation = new URL(document.referrer).origin === window.location.origin;
    } catch (e) { /* An empty/external referrer cannot carry a previous journey's capture. */ }
    if (/^[a-f0-9]{32}$/.test(capture)) {
      journey.acquisition_capture_ref = capture;
    } else if (capture === "" && (!internalNavigation || !stored)) {
      // A plain return has no per-visitor HTML meta on a cacheable page.
      // Recover only the still-fresh first-party capture stored with the UTMs.
      const previous = readFreshUtmData();
      const previousRef = previous && previous.acquisition_capture_ref;
      if (/^[a-f0-9]{32}$/.test(previousRef || "")) {
        journey.acquisition_capture_ref = previousRef;
      } else {
        delete journey.acquisition_capture_ref;
      }
    } else if (capture || !internalNavigation || !stored) {
      delete journey.acquisition_capture_ref;
    }
    pageCaptureInitialized = true;
  }

  journey.last_seen_at = currentTime;
  journey.page_path = currentPath();
  journey.landing_path = sanitizeJourneyPath(journey.landing_path);
  saveJourney(journey);

  return {
    acquisition_capture_ref: journey.acquisition_capture_ref || "",
    journey_id: journey.journey_id,
    landing_path: journey.landing_path,
    page_path: journey.page_path,
  };
}

export function productHint(value) {
  if (value !== undefined && value !== null && value !== "") {
    const explicit = String(value).toLowerCase().replace(/[^a-z0-9_.:-]+/g, "-");
    return explicit.replace(/^-+|-+$/g, "").slice(0, 80);
  }

  const segments = currentPath()
    .toLowerCase()
    .split("/")
    .filter(Boolean)
    .filter((segment) => !["ua", "uk", "ru", "en", "country", "visa", "konsultaciya"].includes(segment));

  return (segments.join(":") || "homepage")
    .replace(/[^a-z0-9_.:-]+/g, "-")
    .slice(0, 80);
}

export function contactAttributionFields(options = {}) {
  const journey = getJourneyContext();
  return {
    acquisition_capture_ref: journey.acquisition_capture_ref,
    journey_id: journey.journey_id,
    contact_event_id: String(options.contactEventId || "").slice(0, 80),
    landing_path: journey.landing_path,
    page_path: journey.page_path,
    contact_method: String(options.contactMethod || "").toLowerCase().slice(0, 20),
    form_subtype: String(options.formSubtype || "").slice(0, 80),
    product_hint: productHint(options.productHint),
    ...measurementChoice(),
  };
}

export function attachContactAttribution(form, options = {}) {
  Object.assign(form, contactAttributionFields(options));
  return form;
}

export function buildMessengerUrl(platform, contactEventId, options = {}) {
  const params = contactAttributionFields({
    ...options,
    contactMethod: platform,
    contactEventId,
  });
  const utm = options.utm || {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id",
    "openai_ad_group_id", "openai_account_id"].forEach((key) => {
    if (typeof utm[key] === "string" && utm[key]) params[key] = utm[key].slice(0, 255);
  });

  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key]) query.set(key, params[key]);
  });
  return "/go/" + platform + "?" + query.toString();
}

export function sendContactAttempt(options = {}) {
  const params = contactAttributionFields(options);
  const utm = options.utm || {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id",
    "openai_ad_group_id", "openai_account_id"].forEach((key) => {
    if (typeof utm[key] === "string" && utm[key]) params[key] = utm[key].slice(0, 255);
  });
  // GA4 is loaded only through Google Tag Manager on this site, so there is no
  // gtag() API on the page. A gtag-style `arguments` push is ignored by GTM and
  // never reached GA4. Emit a plain dataLayer event instead; GTM forwards it to
  // GA4 through the `contact_attempt` Custom Event trigger, which maps these
  // fields to event parameters.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "contact_attempt", ...params });
  return params;
}

// Bind the landing at page load, not at the later moment a contact CTA is clicked.
getJourneyContext();
